// HelpAssistant/useFabDrag.js
//
// Pointer-drag for the floating Help launcher.
//
// WHY not a drag library: the app already carries `react-dnd` (+ its HTML5
// backend) and `react-draggable-list`, but both model LIST reordering over the
// HTML5 drag-and-drop API — which produces a drag ghost image, does not fire on
// touch without a second backend, and has no concept of a free x/y drop point.
// A free-positioned launcher wants raw Pointer Events, which are one API for
// mouse + touch + pen, give pointer capture for free (the drag survives the
// cursor leaving the 56px button), and add zero bytes to the bundle.
//
// Moves are rAF-throttled: pointermove fires far more often than the display
// refreshes, and each move would otherwise be a React render.
//
// Keyboard parity is part of the feature, not a bonus: a drag-only "moveable"
// control is unusable without a pointer. With the FAB focused, the arrow keys
// move it by a fixed step and commit on each press.

import { useCallback, useRef, useState } from "react";
import { clampFabPosition } from "./fabPosition";

/** Pointer travel (px) before a press counts as a drag rather than a click. */
export const DRAG_THRESHOLD = 4;

/** Distance (px) one arrow-key press moves the FAB. */
export const KEYBOARD_STEP = 16;

const ARROW_DELTAS = {
  ArrowLeft: { dx: -KEYBOARD_STEP, dy: 0 },
  ArrowRight: { dx: KEYBOARD_STEP, dy: 0 },
  ArrowUp: { dx: 0, dy: -KEYBOARD_STEP },
  ArrowDown: { dx: 0, dy: KEYBOARD_STEP },
};

/**
 * @param {{left:number, top:number}} position  current resolved position
 * @param {{width:number,height:number,edgeMargin:number}} [box]  launcher box (launcherBoxForWidth)
 * @param {number} obstructionHeight            bottom-docked banner height, 0 if none
 * @param {(pos:{left:number,top:number}) => void} onCommit  persist the new position
 * @returns {{position, dragging, handlers, suppressClick}}
 */
export default function useFabDrag({
  position,
  box,
  obstructionHeight = 0,
  onCommit,
}) {
  const { width: boxWidth, height: boxHeight, edgeMargin: boxEdgeMargin } =
    box || {};
  const [preview, setPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const originRef = useRef(null); // {x, y, left, top}
  const movedRef = useRef(false);
  const rafRef = useRef(null);
  const pendingRef = useRef(null);

  const viewportClamp = useCallback(
    (left, top) =>
      clampFabPosition({
        left,
        top,
        viewportWidth:
          typeof window !== "undefined" ? window.innerWidth : left + 1000,
        viewportHeight:
          typeof window !== "undefined" ? window.innerHeight : top + 1000,
        ...(boxWidth != null
          ? { fabWidth: boxWidth, fabHeight: boxHeight, edgeMargin: boxEdgeMargin }
          : {}),
        obstructionHeight,
      }),
    [obstructionHeight, boxWidth, boxHeight, boxEdgeMargin]
  );

  const cancelFrame = useCallback(() => {
    if (rafRef.current != null && typeof window !== "undefined") {
      if (typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback(
    (event) => {
      // Primary button / touch / pen only — never a right-click or the X button
      // nested inside the launcher (that one stops propagation itself).
      if (typeof event.button === "number" && event.button !== 0) return;
      originRef.current = {
        x: event.clientX,
        y: event.clientY,
        left: position.left,
        top: position.top,
      };
      movedRef.current = false;
      try {
        if (event.pointerId != null && event.currentTarget.setPointerCapture) {
          event.currentTarget.setPointerCapture(event.pointerId);
        }
      } catch (e) {
        /* capture unsupported — the drag still works via bubbling */
      }
    },
    [position.left, position.top]
  );

  const onPointerMove = useCallback(
    (event) => {
      const origin = originRef.current;
      if (!origin) return;
      const dx = event.clientX - origin.x;
      const dy = event.clientY - origin.y;
      // A pointer event without usable coordinates (a synthetic event, a
      // browser that omits them on cancel) must be ignored, never turned into a
      // NaN position that would silently drop the FAB out of the layout.
      if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
      if (!movedRef.current) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
          return;
        }
        movedRef.current = true;
        setDragging(true);
      }
      pendingRef.current = viewportClamp(origin.left + dx, origin.top + dy);
      const apply = () => {
        rafRef.current = null;
        if (pendingRef.current) setPreview(pendingRef.current);
      };
      if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
        apply();
        return;
      }
      if (rafRef.current == null) {
        rafRef.current = window.requestAnimationFrame(apply);
      }
    },
    [viewportClamp]
  );

  const endDrag = useCallback(
    (event) => {
      const origin = originRef.current;
      originRef.current = null;
      cancelFrame();
      if (!origin) return;
      try {
        if (event?.pointerId != null && event.currentTarget?.releasePointerCapture) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      } catch (e) {
        /* nothing captured */
      }
      if (!movedRef.current) {
        setDragging(false);
        return;
      }
      const dropped =
        pendingRef.current ||
        viewportClamp(
          origin.left + (event?.clientX ?? origin.x) - origin.x,
          origin.top + (event?.clientY ?? origin.y) - origin.y
        );
      pendingRef.current = null;
      setPreview(null);
      setDragging(false);
      if (onCommit) onCommit(dropped);
    },
    [cancelFrame, onCommit, viewportClamp]
  );

  const onKeyDown = useCallback(
    (event) => {
      const delta = ARROW_DELTAS[event.key];
      if (!delta) return;
      event.preventDefault();
      const next = viewportClamp(position.left + delta.dx, position.top + delta.dy);
      if (onCommit) onCommit(next);
    },
    [onCommit, position.left, position.top, viewportClamp]
  );

  /**
   * True exactly once after a drag, so the synthetic click that follows
   * pointerup does not also toggle the panel open. Consumes the flag.
   */
  const suppressClick = useCallback(() => {
    if (!movedRef.current) return false;
    movedRef.current = false;
    return true;
  }, []);

  return {
    // While dragging, the preview wins; otherwise the resolved position does.
    position: preview || position,
    dragging,
    suppressClick,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
    },
  };
}
